import store from '@/store'
import actions from '@/store/actions'
import mutations from '@/store/mutations'

export const getContract = async (contractId) => {
  const { contract, canUserEdit, canUserComment } = await store.dispatch(actions.callApi, { url: `contracts/${contractId}` })
  contract.chosenCurrency = contract.currencies[0]
  contract.workflows = contract.workflows
    .filter(i => i.type === 'payment')
    .map(item => {
      const { fieldsToFill } = store.state.workflowsTypes.find(i => i.type === 'payment')
      return {
        cardTitle: item.name,
        ...item,
        workflowSteps: item.workflowSteps.map(i => ({
          ...i,
          fieldsToFill: i.fieldsToFill.map(name => fieldsToFill.find(field => field.key === name))
        })).sort((a, b) => a.order - b.order)
      }
    })
  contract.signedPriceCents = Number(contract.signedPriceCents)

  await store.commit(mutations.SET_CAN_USER_PERMISSIONS, { canUserEdit, canUserComment })
  return contract
}

export const getContracts = async () => {
  const contracts = await store.dispatch(actions.callApi, { url: 'contracts' })
  await store.commit(mutations.SET_CONTRACTS, contracts.map(contract => {
    return {
      ...contract,
      chosenCurrency: contract.currencies[0],
      subContracts: contract.subContracts.map(i => ({
        ...i,
        chosenCurrency: i.currencies[0]
      }))
    }
  }))
}

export const createNewContract = async (data) => {
  delete data.chosenCurrency
  delete data.workflows
  delete data.purchaseOrderList
  delete data.parentContract

  const contract = await store.dispatch(actions.callApi, { url: 'contracts', method: 'post', body: data })
  contract.chosenCurrency = contract.currencies[0]
  contract.purchaseOrderList = []
  store.dispatch(actions.createMessage, { text: 'Contract created', color: 'success' })
  return contract
}

export const updateContract = async (data) => {
  const contract = await store.dispatch(actions.callApi, {
    url: `contracts/${data.id}`,
    method: 'put',
    body: {
      commencementDate: data.commencementDate,
      completionTime: data.completionTime,
      contractManager: data.contractManager,
      contractName: data.contractName,
      contractType: data.contractType,
      contractor: data.contractor || {},
      currencies: data.currencies,
      employer: data.employer || {},
      package: data.package,
      packageManager: data.packageManager,
      projectController: data.projectController,
      priceBreakdown: data.priceBreakdown,
      purchaseOrder: data.purchaseOrder,
      signatureDate: data.signatureDate,
      signedPriceCents: data.signedPriceCents,
      project: data.project || {},
      parts: data.parts,
      takingOverDate: data.takingOverDate,
      isParent: data.isParent,
      subContracts: data.subContracts,
      parentContractId: data.parentContractId,
      isMultiCurrency: data.isMultiCurrency,
      liquidatedDamagesCap: data.liquidatedDamagesCap
    }
  })
  contract.chosenCurrency = contract.currencies[0]
  store.dispatch(actions.createMessage, { text: 'Contract updated', color: 'success' })
  return contract
}

export const addDocumentToContract = async (data, contractId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/documents`,
    method: 'post',
    body: data
  })
  store.dispatch(actions.createMessage, { text: 'Document added', color: 'success' })
}

export const addFlow = async (type, data, contractId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/workflows`,
    method: 'post',
    body: {
      name: data.name,
      type,
      workflowSteps: data.workflowSteps.map((step, index) => ({
        id: step.id,
        dueInDays: step.dueInDays,
        name: step.name,
        notifications: step.notifications,
        calculationBase: step.calculationBase && step.calculationBase.id ? step.calculationBase.id : step.calculationBase,
        order: index,
        fieldsToFill: []
      }))
    }
  })
  store.dispatch(actions.createMessage, { text: 'Workflow added', color: 'success' })
}

export const updateFlow = async (type, data, contractId) => {
  const flow = await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/workflows/${data.id}`,
    method: 'put',
    body: {
      name: data.name,
      type,
      workflowSteps: data.workflowSteps.map((step, index) => ({
        dueInDays: step.dueInDays,
        name: step.name,
        notifications: step.notifications,
        order: index,
        calculationBase: typeof step.calculationBase === 'object' ? step.calculationBase.id : step.calculationBase,
        id: step.id || undefined,
        fieldsToFill: step.fieldsToFill ? step.fieldsToFill.map(i => i.key) : []
      }))
    }
  })
  flow.cardTitle = flow.name
  flow.workflowSteps = flow.workflowSteps.map((i, index) => ({ ...i, fieldsToFill: data.workflowSteps[index].fieldsToFill }))
  store.dispatch(actions.createMessage, { text: 'Workflow updated', color: 'success' })
  return flow
}

export const addNewStep = async (flowId, contractId, data, type) => {
  const step = await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/workflows/${flowId}/steps`,
    method: 'post',
    body: {
      dueInDays: data.dueInDays,
      name: data.name,
      notifications: data.notifications,
      calculationBase: data.calculationBase.step,
      order: data.order,
      fieldsToFill: data.fieldsToFill || []
    }
  })
  return step
}

export const deleteFlow = async (type, data, contractId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/workflows/${data.id}`,
    method: 'delete'
  })
  store.dispatch(actions.createMessage, { text: 'Flow deleted', color: 'success' })
}

export const deleteFlowStep = async (type, step, contractId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/workflows/${step.workflowId}/steps/${step.id}`,
    method: 'delete'
  })
}

export const toggleContractStatus = async (contractId) => {
  await store.dispatch(actions.callApi, {
    url: `contracts/${contractId}/status`,
    method: 'patch'
  })
  store.dispatch(actions.createMessage, { text: 'Contract status changed', color: 'success' })
}
